import { TrackingUserData } from '~/lib/tracking';

declare global {
  interface Window {
    gtag?: (
      command: string,
      action: string,
      params?: Record<string, unknown>
    ) => void;
  }
}

export function useGoogleAnalytics() {
  // Initialize Google Analytics
  const init = async (gaId: string) => {
    if (typeof window === 'undefined' || !gaId) return Promise.resolve();

    try {
      // Check if the GA script is already loaded
      if (!document.getElementById(`google-analytics-${gaId}`)) {
        // Load the Google Analytics script
        const script = document.createElement('script');
        script.id = `google-analytics-${gaId}`;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
        script.async = true;
        document.head.appendChild(script);

        // Initialize gtag
        window.dataLayer = window.dataLayer || [];
        function gtag(...args: unknown[]) {
          window.dataLayer?.push(args);
        }
        gtag('js', new Date());
        gtag('config', gaId);

        // Make gtag available globally
        window.gtag = (
          command: string,
          action: string,
          params?: Record<string, unknown>
        ) => {
          gtag(command, action, params);
        };
      }

      return Promise.resolve();
    } catch (error) {
      console.error('Error initializing Google Analytics:', error);
      return Promise.reject(error);
    }
  };

  // Identify user in Google Analytics
  const identify = (userId: number, userData: TrackingUserData) => {
    if (!userId || typeof window === 'undefined' || !window.gtag) return;

    // Set user ID and properties in Google Analytics
    window.gtag('config', '', {
      user_id: userId,
    });

    // Set user properties in Google Analytics using a custom event
    window.gtag('event', 'user_data', userData);
  };

  // Track event in Google Analytics
  const track = (eventName: string, properties?: Record<string, unknown>) => {
    if (typeof window === 'undefined' || !window.gtag) return;

    // Convert event name to GA4 format (snake_case)
    const formattedEventName = eventName.toLowerCase().replace(/\s+/g, '_');

    // Track the event
    window.gtag('event', formattedEventName, properties);
  };

  return { init, identify, track };
}
